package com.iptvplayer.shared.presentation.state

import com.iptvplayer.shared.domain.model.*

data class AuthUiState(
    val isLoading: Boolean = false,
    val isLoggedIn: Boolean = false,
    /** True once the stored session has been checked — gate splash screens on this. */
    val isInitialized: Boolean = false,
    val user: User? = null,
    val error: String? = null
)

data class HomeUiState(
    val isLoading: Boolean = false,
    /** Top-of-catalog rows only (windowed) — Home never holds the full catalog. */
    val channels: List<Channel> = emptyList(),
    val vodItems: List<VodItem> = emptyList(),
    val series: List<SeriesItem> = emptyList(),
    /** Pre-built "first N channels of the top categories" rows. */
    val categoryRows: List<HomeCategoryRow> = emptyList(),
    val continueWatching: List<WatchHistory> = emptyList(),
    /** Why content may be missing (no sources / provider down / …) — see [ContentIssue]. */
    val issue: ContentIssue? = null
)

/** One Home category shelf: a category name plus its first few channels. */
data class HomeCategoryRow(
    val name: String,
    val channels: List<Channel>
)

data class BrowseUiState(
    val isLoading: Boolean = false,
    val channels: List<Channel> = emptyList(),
    val vodItems: List<VodItem> = emptyList(),
    val series: List<SeriesItem> = emptyList(),
    val categories: List<String> = emptyList(),
    val selectedCategory: String? = null,
    val searchQuery: String = "",
    val error: String? = null
)

data class PlayerUiState(
    val isPlaying: Boolean = false,
    val isBuffering: Boolean = false,
    val currentPositionMs: Long = 0,
    val durationMs: Long = 0,
    val contentName: String = "",
    /** Poster/logo of what's playing — drives the branded loading backdrop. Kept in sync on zap. */
    val contentLogoUrl: String? = null,
    val streamUrl: String = "",
    val isFavorite: Boolean = false,
    val error: String? = null,
    /** True when playing a live channel (enables zapping, hides seek). */
    val isLiveChannel: Boolean = false,
    /** 0 = fit, 1 = zoom (crop), 2 = stretch. */
    val resizeMode: Int = 0,
    /** Incremented to ask the platform player to re-prepare after an error. */
    val retryToken: Int = 0,
    val audioTracks: List<MediaTrack> = emptyList(),
    val textTracks: List<MediaTrack> = emptyList(),
    /** Video quality variants (ABR streams); empty when the stream has one rendition. */
    val videoTracks: List<MediaTrack> = emptyList(),
    val selectedAudioId: String? = null,
    val selectedTextId: String? = null,
    /** null = Auto (adaptive). */
    val selectedVideoId: String? = null,
    /** VOD finished and there is nothing to auto-play next — screen should close. */
    val playbackCompleted: Boolean = false,
    /** Playing a series episode with known series context (enables the Next-episode button). */
    val isSeriesEpisode: Boolean = false,
    /** Label of the episode that just auto-started ("Up next" toast); cleared after a few seconds. */
    val upNext: String? = null
)

/** A selectable audio/subtitle track reported by the platform player. */
data class MediaTrack(
    val id: String,
    val label: String
)

data class SourceUiState(
    val isLoading: Boolean = false,
    val sources: List<IptvSource> = emptyList(),
    val error: String? = null
)

data class FavoritesUiState(
    val isLoading: Boolean = false,
    val favorites: List<Favorite> = emptyList(),
    val error: String? = null
)

data class HistoryUiState(
    val isLoading: Boolean = false,
    val history: List<WatchHistory> = emptyList(),
    val error: String? = null
)

data class DeviceUiState(
    val isLoading: Boolean = false,
    val devices: List<Device> = emptyList(),
    val currentDeviceId: String? = null,
    val error: String? = null
)

data class RemotePlayUiState(
    val isLoading: Boolean = false,
    val onlineDevices: List<Device> = emptyList(),
    val selectedDeviceId: String? = null,
    val commandSent: Boolean = false,
    val error: String? = null
)
